import { useLocalization } from '@components/Localization'
import LocalizedLink from '@components/Localization/LocalizedLink'
import { Box, Button, Flex, Heading, Spinner, Text, theme } from '@gassan-ui'
import { formatPrice } from '@lib/formatPrice'
import { useCart } from '@lib/hooks/useCart'
import { motion } from 'framer-motion'
import { rgba } from 'polished'
import React, { FC } from 'react'
import styled from 'styled-components'
import Logos from './Logos'
import USPs from './USPs'

const Parent = styled(motion.custom(Box))``

type Props = {
  status: 'updating' | 'idle'
}

const CartSummary: FC<Props> = ({ status }) => {
  const { lang, messages } = useLocalization()
  const cart = useCart()

  return (
    <>
      <Parent
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        p={['1.5rem 1rem', '2.5rem 3rem']}
        bg="pampasLight"
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          pb={['1rem', '1.5rem']}
          mb={['1rem', '1.5rem']}
          borderBottom={`1px solid ${rgba(theme.colors.tabaccoBrown, 0.2)}`}
        >
          <Heading variant="h5" mb="0">
            {lang === 'nl' ? 'Bezorging' : 'Delivery'}
          </Heading>
          <Text color="shades.800" mb="0">
            {lang === 'nl' ? 'Gratis in Nederland' : 'Free in the Netherlands'}
          </Text>
        </Flex>
        <Flex
          justifyContent="space-between"
          alignItems="flex-start"
          pb={['1rem', '1.5rem']}
          mb={['1rem', '1.5rem']}
          borderBottom={`1px solid ${rgba(theme.colors.tabaccoBrown, 0.2)}`}
        >
          <Box>
            <Heading variant="h5" mb="0">
              {lang === 'nl' ? 'Totaal' : 'Total'}
            </Heading>
            <Text
              variant="small"
              color={rgba(theme.colors.tabaccoBrown, 0.6)}
              mb="0"
              minHeight={22}
            >
              {cart && (
                <>
                  {lang === 'nl'
                    ? `Inclusief ${formatPrice(cart.totalTax)} BTW`
                    : `Including ${formatPrice(cart.totalTax)} VAT`}
                </>
              )}
            </Text>
          </Box>
          <Box>
            {status === 'updating' ? (
              <Box color="tabaccoBrown">
                <Spinner size={['0.75rem', '1.5rem']} />
              </Box>
            ) : (
              <Text variant="large" mb="0">
                {cart && formatPrice(cart.totalPriceIncTax, true)}
              </Text>
            )}
          </Box>
        </Flex>
        <LocalizedLink href={messages.navigation.checkout.order}>
          <Button
            width="100%"
            variant="dark"
            status={status === 'updating' ? 'disabled' : 'idle'}
            as="link"
          >
            {lang === 'nl' ? 'Afrekenen' : 'Order'}
          </Button>
        </LocalizedLink>
        <USPs />
        <Logos />
      </Parent>
    </>
  )
}

export default CartSummary
